import {
    Box,
    Flex,
    Spacer,
    Icon,
    Image,
    Tooltip,
    Badge,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    Text,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useContext, useState } from 'react';
import { useBreadcrumbs } from '../contexts/BreadcrumbContext';
import { TbMenu2, TbX } from 'react-icons/tb';
import YaxleLogo from '../assets/yaxleLogoBlue.png';
import SearchBar, { TOPBAR_SEARCH_BAR_BREAKPOINT, TOPBAR_SEARCH_WIDTH } from './SearchBar';
import { useGetNotifications } from '../hooks/useGetNotifications';
import { NotificationDrawer } from './notifications';
import OrganizationSwitcherButton from './OrganizationSwitcherButton';
import SessionContext, { isInventoryOrgSession } from '../contexts/SessionContext';
import { usePendingPurchaseOrdersCount } from '../hooks/inventory/usePendingPurchaseOrdersCount';
import { PendingCountBadge } from './PendingCountBadge';
import { Icon as YaxleIcon, IconButton } from '../ui';
import {
    invoicesListHref,
    loadInvoicesViewStateCache,
    withoutInvoicesSearch,
} from '../shared/invoicesListViewState';

interface TopBarProps {
    isSidebarCollapsed: boolean;
    onToggleSidebar: () => void;
    /** Mobile layout: hamburger opens the overlay nav instead of collapsing the rail. */
    isOverlayNav?: boolean;
    isNavOpen?: boolean;
}

/**
 * Widest the left cluster may grow at laptop+ before it would run under the
 * centered search bar: half the row, minus half the bar, minus breathing room.
 * When a side drawer is open, `index.css` overrides this against the
 * drawer-adjusted visible area so the recentered bar stays clickable.
 */
const LEFT_CLUSTER_MAX_WIDTH = `calc(50% - (${TOPBAR_SEARCH_WIDTH}) / 2 - 1.5rem)`;

/**
 * Top bar (ENG-2297) — sits beside the full-height nav rail, painted the same
 * as the page so it reads as part of the content. Left: merged breadcrumb /
 * page-title row (">" separators). Center: the global search bar (laptop and
 * up; collapses to an icon in the right cluster below that). Right: messages,
 * bell. The account menu lives at the head of the sidebar (see UserMenu).
 */
const TopBar = ({
    isSidebarCollapsed,
    onToggleSidebar,
    isOverlayNav = false,
    isNavOpen = false,
}: TopBarProps) => {
    const navigate = useNavigate();
    const { items: breadcrumbItems, currentPage } = useBreadcrumbs();
    const sessionContext = useContext(SessionContext);
    const [isNotificationDrawerOpen, setIsNotificationDrawerOpen] = useState(false);
    const { data: notificationsData } = useGetNotifications();
    const isInvoicingOrg =
        sessionContext?.isAuthenticated === true && sessionContext.organizationMode === 'invoicing';
    const isInvoicingRole = sessionContext?.isAuthenticated && !sessionContext.permissions.some(p => p !== 'invoicing');
    const isTechnicianRole = sessionContext?.isAuthenticated && !sessionContext.permissions.some(p => p !== 'inventory');
    const isInventoryOrg = isInventoryOrgSession(sessionContext);
    const logoNavigatesToInvoices = isInvoicingOrg || isInvoicingRole;
    const logoNavigatesToInventory = isInventoryOrg || isTechnicianRole;
    const showInventoryPendingDot =
        logoNavigatesToInventory || sessionContext?.permissions?.includes('inventory');
    const { total: pendingPurchaseOrdersCount } =
        usePendingPurchaseOrdersCount(showInventoryPendingDot);
    /** Communications/Chat is hidden for IT, invoicing orgs/roles, inventory orgs, and technicians (matches the /chat route guard). */
    const canUseChat = !isInvoicingOrg && !isInventoryOrg && !isInvoicingRole && !isTechnicianRole && sessionContext?.isAuthenticated && sessionContext;

    /** The rail owns branding on desktop; the topbar shows it on mobile overlay. */
    const showBrandBlock = isOverlayNav;
    /** Parent sidebar-tab views set a page title with no trail (`usePageTitle`). */
    const isParentView = breadcrumbItems.length === 0;
    const railClass = showBrandBlock
        ? 'partbay-topbar--rail-none'
        : isSidebarCollapsed
            ? 'partbay-topbar--rail-collapsed'
            : 'partbay-topbar--rail-expanded';
    const topbarClass = [
        'partbay-topbar',
        railClass,
        isParentView ? 'partbay-topbar--viewport-center' : '',
    ].filter(Boolean).join(' ');

    return (
        <>
            <Box
                className={topbarClass}
                position="relative"
                flexShrink={0}
                h="topbar"
                bg="white"
                borderBottom="1px solid"
                borderColor="gray.200"
                zIndex={10}
            >
                <Flex
                    className="partbay-topbar-row"
                    h="100%"
                    alignItems="center"
                    pl={showBrandBlock ? 0 : 6}
                    pr={4}
                    gap={2}
                >
                    {/* Left cluster — brand (when there is no rail) + breadcrumb trail.
                        Capped at laptop+ so a long trail truncates instead of running
                        under the centered search bar. */}
                    <Flex
                        className="partbay-topbar-left"
                        alignItems="center"
                        gap={2}
                        flexShrink={1}
                        minW={0}
                        overflow="hidden"
                        maxW={{ base: 'none', [TOPBAR_SEARCH_BAR_BREAKPOINT]: LEFT_CLUSTER_MAX_WIDTH }}
                        zIndex={7}
                    >
                        {showBrandBlock && (
                            <Box
                                flexShrink={0}
                                px={3}
                                display="flex"
                                alignItems="center"
                                gap={1}
                            >
                                {isOverlayNav && (
                                    <Box
                                        as="button"
                                        type="button"
                                        aria-label={isNavOpen ? 'Close navigation' : 'Open navigation'}
                                        aria-expanded={isNavOpen}
                                        aria-controls="partbay-nav-drawer"
                                        onClick={onToggleSidebar}
                                        w="2.5rem"
                                        h="2.5rem"
                                        borderRadius="md"
                                        display="inline-flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        _hover={{ bg: 'gray.200' }}
                                        _active={{ bg: 'gray.300' }}
                                        transition="background-color 180ms ease"
                                    >
                                        <Icon
                                            as={(isNavOpen ? TbX : TbMenu2) as React.ElementType}
                                            w="1.5rem"
                                            h="1.5rem"
                                            color="gray.700"
                                        />
                                    </Box>
                                )}
                                {logoNavigatesToInvoices ? (
                                    <Tooltip label="Invoices" placement="bottom-start" openDelay={150}>
                                        <Box
                                            as="button"
                                            type="button"
                                            aria-label="Go to invoices"
                                            onClick={() => {
                                                const invoicesViewState = withoutInvoicesSearch(loadInvoicesViewStateCache());
                                                navigate(
                                                    invoicesListHref(invoicesViewState),
                                                    invoicesViewState ? { state: { invoicesViewState } } : undefined,
                                                );
                                            }}
                                            w="2.5rem"
                                            h="2.5rem"
                                            borderRadius="md"
                                            display="inline-flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            _hover={{ bg: 'gray.200' }}
                                            _active={{ bg: 'gray.300' }}
                                            transition="background-color 180ms ease"
                                        >
                                            <Image src={YaxleLogo} alt="Yaxle" boxSize="2rem" objectFit="contain" />
                                        </Box>
                                    </Tooltip>
                                ) : logoNavigatesToInventory ? (
                                    <Tooltip label="Inventory" placement="bottom-start" openDelay={150}>
                                        <Box
                                            as="button"
                                            type="button"
                                            aria-label="Go to inventory"
                                            onClick={() => navigate('/inventory')}
                                            w="2.5rem"
                                            h="2.5rem"
                                            borderRadius="md"
                                            display="inline-flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            position="relative"
                                            overflow="visible"
                                            _hover={{ bg: 'gray.200' }}
                                            _active={{ bg: 'gray.300' }}
                                            transition="background-color 180ms ease"
                                        >
                                            <Image src={YaxleLogo} alt="Yaxle" boxSize="2rem" objectFit="contain" />
                                            <PendingCountBadge count={pendingPurchaseOrdersCount} />
                                        </Box>
                                    </Tooltip>
                                ) : (
                                    <Box
                                        w="2.5rem"
                                        h="2.5rem"
                                        borderRadius="md"
                                        display="inline-flex"
                                        alignItems="center"
                                        justifyContent="center"
                                    >
                                        <Image src={YaxleLogo} alt="Yaxle" boxSize="2rem" objectFit="contain" />
                                    </Box>
                                )}
                            </Box>
                        )}

                        {/* Merged header — breadcrumb trail with the page title as its last crumb. */}
                        {(breadcrumbItems.length > 0 || currentPage) && (
                            <Box
                                flexShrink={1}
                                minW={0}
                                overflow="hidden"
                            >
                                <Breadcrumb
                                    fontSize="sm"
                                    color="gray.500"
                                    whiteSpace="nowrap"
                                    separator={<YaxleIcon name="chevronRight" size="xs" color="fg.muted" />}
                                >
                                    {breadcrumbItems.map((item, index) => (
                                        <BreadcrumbItem key={index}>
                                            <BreadcrumbLink
                                                onClick={() => navigate(item.path, item.state !== undefined ? { state: item.state } : undefined)}
                                                _hover={{ textDecoration: 'none', color: '#0071E3' }}
                                                cursor="pointer"
                                            >
                                                {item.label}
                                            </BreadcrumbLink>
                                        </BreadcrumbItem>
                                    ))}
                                    {currentPage && (
                                        <BreadcrumbItem isCurrentPage>
                                            <Text
                                                fontWeight={breadcrumbItems.length > 0 ? 'bold' : 'semibold'}
                                                color="gray.800"
                                                fontSize={breadcrumbItems.length > 0 ? 'sm' : 'lg'}
                                                noOfLines={1}
                                            >
                                                {currentPage}
                                            </Text>
                                        </BreadcrumbItem>
                                    )}
                                </Breadcrumb>
                            </Box>
                        )}
                    </Flex>

                    <Spacer minW={2} />

                    {/* Right cluster — compact search (below laptop), messages, notifications.
                        CSS `gap` rather than Stack spacing so SearchBar's absolutely
                        positioned centered bar is not nudged by a sibling margin. */}
                    <Flex
                        className="partbay-topbar-actions"
                        alignItems="center"
                        flexShrink={0}
                        zIndex={7}
                        gap={2}
                    >
                        <OrganizationSwitcherButton />

                        <SearchBar />

                        {/* Messages -- icon that redirects to the /chat Communications tab.
                            Hidden for IT and invoicing orgs/roles. The unread badge counts
                            unread message notifications (order-mention + vendor-message). */}
                        {canUseChat && (
                            <Box position="relative">
                                <Tooltip label="Messages" placement="bottom" openDelay={150}>
                                    <IconButton
                                        aria-label="Messages"
                                        icon={<YaxleIcon name="message" size="lg" />}
                                        variant="ghost"
                                        color="fg.muted"
                                        _hover={{ bg: 'bg.subtle', color: 'fg.default' }}
                                        onClick={() => navigate('/chat')}
                                    />
                                </Tooltip>
                                {notificationsData && (notificationsData.unreadMentionCount ?? 0) > 0 && (
                                    <Badge
                                        as="button"
                                        onClick={() => navigate('/chat', { state: { focusUnread: true } })}
                                        aria-label="View unread messages"
                                        position="absolute"
                                        top="3px"
                                        right="1px"
                                        bg="red.500"
                                        color="white"
                                        borderRadius="full"
                                        minW="14px"
                                        h="14px"
                                        fontSize="9px"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        fontWeight="bold"
                                        cursor="pointer"
                                        zIndex={1}
                                    >
                                        {(notificationsData.unreadMentionCount ?? 0) > 99
                                            ? '99+'
                                            : notificationsData.unreadMentionCount}
                                    </Badge>
                                )}
                            </Box>
                        )}

                        {/* Notification Bell */}
                        <Box position="relative">
                            <IconButton
                                aria-label="Notifications"
                                icon={<YaxleIcon name="bell" size="lg" />}
                                variant="ghost"
                                color="fg.muted"
                                _hover={{ bg: 'bg.subtle', color: 'fg.default' }}
                                onClick={() => setIsNotificationDrawerOpen(true)}
                            />
                            {notificationsData && notificationsData.notificationsEnabled && notificationsData.unreadCount > 0 && (
                                <Badge
                                    position="absolute"
                                    top="3px"
                                    right="1px"
                                    bg="red.500"
                                    color="white"
                                    borderRadius="full"
                                    minW="14px"
                                    h="14px"
                                    fontSize="9px"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    fontWeight="bold"
                                >
                                    {notificationsData.unreadCount > 99 ? '99+' : notificationsData.unreadCount}
                                </Badge>
                            )}
                        </Box>
                    </Flex>
                </Flex>
            </Box>

            {/* Notification Drawer */}
            <NotificationDrawer
                isOpen={isNotificationDrawerOpen}
                onClose={() => setIsNotificationDrawerOpen(false)}
            />
        </>
    );
};

export default TopBar;
